import { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { MdCheckCircleOutline } from "react-icons/md";
import terrazzo1 from "../assets/Terrazzo/banner1.jpeg";
import terrazzo2 from "../assets/Terrazzo/banner2.jpg";
import terrazzo3 from "../assets/Terrazzo/banner3.jpg";
import terrazzo4 from "../assets/Terrazzo/banner4.jpg";
import terrazzo5 from "../assets/Terrazzo/banner5.jpeg";
import terrazzo6 from "../assets/Terrazzo/banner6.jpg";
import terrazzo7 from "../assets/Terrazzo/banner7.jpg";
import terrazzo8 from "../assets/Terrazzo/banner8.jpg";

export default function TerrazzoFlooringSolutions() {
  // Independent image sets for each slider
  const slider1Images = [terrazzo1, terrazzo2, terrazzo3, terrazzo4];
  const slider2Images = [terrazzo5, terrazzo6, terrazzo7, terrazzo8];

  // Independent state for each slider
  const [slider1Index, setSlider1Index] = useState(0);
  const [slider2Index, setSlider2Index] = useState(0);

  const challenges = [
    "Rapid wear in high-traffic areas",
    "Cracking or joint failure in tiled finishes",
    "Stained or deteriorating surfaces in public spaces",
    "High maintenance costs associated with alternative floor finishes",
    "Limited design flexibility with standard flooring materials",
  ];

  const whyTerrazzo = [
    {
      title: "Seamless, Monolithic Finish",
      detail:
        "Minimizes joints and weak points, reducing cracking and long-term maintenance issues.",
    },
    {
      title: "High Wear Resistance",
      detail:
        "Suitable for schools, hospitals, offices, retail areas and other heavy-use environments.",
    },
    {
      title: "Customizable Design",
      detail:
        "Flexible color palettes and aggregate combinations allow alignment with architectural themes and brand identity.",
    },
  ];

  const applications = [
    "Schools and universities",
    "Hospitals and healthcare facilities",
    "Retail spaces and shopping complexes",
    "Public and institutional buildings",
    "Residential homes",
    "Office buildings",
  ];

  const projectValue = [
    "Long-lasting, durable floor performance",
    "Reduced maintenance and replacement costs",
    "Refined architectural finish",
    "Improved lifecycle value of your building",
  ];

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 700,
      easing: "ease-out-cubic",
      once: false,
      offset: 80,
    });
    AOS.refresh();
  }, []);

  // Slider 1: Fade transition (3.5s interval)
  useEffect(() => {
    if (slider1Images.length <= 1) return;
    const timer = setInterval(() => {
      setSlider1Index((prev) => (prev + 1) % slider1Images.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [slider1Images.length]);

  // Slider 2: Horizontal scroll transition (5s interval)
  useEffect(() => {
    if (slider2Images.length <= 1) return;
    const timer = setInterval(() => {
      setSlider2Index((prev) => (prev + 1) % slider2Images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slider2Images.length]);

  const IconList = ({ items }) => (
    <ul className="tz-icon-list">
      {items.map((item) => (
        <li key={item} className="tz-icon-list-item">
          <MdCheckCircleOutline size={19} className="tz-list-icon" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <section className="MainBodySection py-5 mt-5 tz-page">
      <div className="container">
        <div className="row g-3">
          <div className="col-md-7 d-flex" data-aos="fade-up">
            <div className="rounded mShadow2 bg-light p-3 tz-equal-card w-100">
              <div className="text-primary font-bold fs-3">
                TERRAZZO FLOORING SOLUTIONS
              </div>
              <div className="text-muted font-semi-bold">
                <p>Durable Architectural Finishes for High-Traffic Environments</p>
              </div>
              <p>
                Floor finishes in commercial and institutional buildings must do more than look good, they must
                withstand constant foot traffic, resist wear and maintain their appearance over time.
                <br />
                Our terrazzo flooring solutions deliver a seamless, high-performance finish that combines aesthetic
                flexibility with long-term durability.
                <br />
                Designed for both new construction and renovation projects, terrazzo provides a refined architectural
                look without the maintenance challenges of natural stone.
              </p>
            </div>
          </div>

          {/* Slider 1: Fade Transition */}
          <div
            className="col-md-5 d-flex"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <div className="w-100 tz-slider-container-1">
              {slider1Images.map((image, idx) => (
                <div
                  key={`slider1-${idx}`}
                  className={`tz-slider-slide-1 ${idx === slider1Index ? "active" : ""}`}
                  style={{ backgroundImage: `url(${image})` }}
                  role="img"
                  aria-label={`Terrazzo slider 1 image ${idx + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Slider 2: Horizontal Scroll Transition */}
          <div
            className="col-md-5 mt-3 d-flex"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            <div className="w-100 tz-slider-container-2">
              <div
                className="tz-slider-track-2"
                style={{ transform: `translateX(-${slider2Index * 100}%)` }}
              >
                {slider2Images.map((image, idx) => (
                  <div
                    key={`slider2-${idx}`}
                    className="tz-slider-slide-2"
                    style={{ backgroundImage: `url(${image})` }}
                    role="img"
                    aria-label={`Terrazzo slider 2 image ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="col-md-7 mt-3 d-flex" data-aos="fade-up">
            <div className="rounded mShadow2 bg-light p-3 w-100 tz-equal-card">
              <div className="text-primary font-bold">
                <p>The Flooring Challenges We Solve</p>
              </div>
              <IconList items={challenges} />
            </div>
          </div>

          <div className="col-md-12 text-center mt-5" data-aos="fade-up">
            <p className="font-bold text-primary mt-3">Why Terrazzo Works</p>
          </div>

          {whyTerrazzo.map((item) => (
            <div
              className="col-md-4 p-2 m-0 d-flex"
              key={item.title}
              data-aos="fade-up"
            >
              <div className="bg-light rounded p-3 text-center mShadow2 tz-equal-card w-100">
                <div className="text-primary font-bold">
                  <p className="font-semi-bold">{item.title}</p>
                </div>
                <p>{item.detail}</p>
              </div>
            </div>
          ))}

          <div
            className="col-md-12 bg-surface p-2 text-center my-5 rounded"
            data-aos="fade-up"
          >
            <div>
              <p
                className="text-primary font-bold fs-3"
                style={{ color: "var(--primaryColor) !important" }}
              >
                Long Lifecycle Performance
              </p>
            </div>
            <p>
              Offers extended service life with minimal maintenance, making it a
              cost-effective long-term flooring solution.
            </p>
          </div>

          <div className="col-md-6 d-flex" data-aos="fade-up">
            <div className="w-100 bg-light rounded mShadow2 p-3 tz-equal-card">
              <div className="text-primary font-bold">
                <p className="font-semi-bold">Ideal Applications</p>
              </div>
              <IconList items={applications} />
            </div>
          </div>
          <div className="col-md-6 d-flex" data-aos="fade-up">
            <div className="w-100 bg-light rounded mShadow2 p-3 tz-equal-card">
              <div className="text-primary font-bold">
                <p className="font-semi-bold">The Value to Your Project</p>
              </div>
              <IconList items={projectValue} />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .tz-page {
          background: radial-gradient(
              circle at top right,
              rgba(15, 118, 110, 0.08),
              transparent 45%
            ),
            #f8fafc;
        }

        .tz-equal-card {
          display: flex;
          flex-direction: column;
          height: 100%;
          border-radius: 16px;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
          border: 1px solid rgba(148, 163, 184, 0.18);
        }

        /* ========== Slider 1: Fade Transition ========== */
        .tz-slider-container-1 {
          position: relative;
          min-height: 230px;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.14);
          border: 1px solid rgba(148, 163, 184, 0.2);
        }

        .tz-slider-slide-1 {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          opacity: 0;
          transition: opacity 0.8s ease-in-out;
        }

        .tz-slider-slide-1.active {
          opacity: 1;
          z-index: 1;
        }

        /* ========== Slider 2: Horizontal Scroll Transition ========== */
        .tz-slider-container-2 {
          position: relative;
          min-height: 230px;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.14);
          border: 1px solid rgba(148, 163, 184, 0.2);
        }

        .tz-slider-track-2 {
          display: flex;
          height: 100%;
          transition: transform 0.8s cubic-bezier(0.25, 1, 0.5, 1);
        }

        .tz-slider-slide-2 {
          min-width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          flex-shrink: 0;
        }

        /* ========== Shared Styles ========== */
        .tz-icon-list {
          margin: 0;
          padding: 0;
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .tz-icon-list-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          line-height: 1.6;
          font-size: 15px;
          color: #1f2937;
        }

        .tz-list-icon {
          color: #0f766e;
          margin-top: 2px;
          flex-shrink: 0;
        }

        .tz-page .text-primary {
          color: #0f172a !important;
        }

        .tz-page .text-muted {
          color: #475569 !important;
        }

        @media (max-width: 767px) {
          .tz-slider-container-1,
          .tz-slider-container-2 {
            min-height: 160px;
          }
        }
      `}</style>
    </section>
  );
}